export type DocFileType =
  | "pdf"
  | "xlsx"
  | "xls"
  | "docx"
  | "doc"
  | "pptx"
  | "pptm"
  | "ppt"
  | "csv"
  | "md"
  | "txt";

// ── Study Documents (from Teams Channel, read-only) ──────────────────────────
export interface StudyDocument {
  id: string;
  name: string;
  type: DocFileType;
  size: string;
  syncedAt: string;
  versionDate: string;
  category: string;
  studyCode: string;
  studyName: string;
  description: string;
  channelName: string;
  version: string;
  site?: string; // undefined = study-wide
  visit?: string; // undefined = not visit-specific
}

// ── Study Sync Status (for Documents page sync status dashboard) ────────────
export interface StudySyncStatus {
  studyCode: string; // may be empty string if not assigned
  studyName: string;
  channelName: string;
  channelUrl?: string;
  totalDocs: number;
  lastSyncedAt: string;
  syncStatus: "synced" | "syncing" | "error" | "pending";
  syncHealth: "healthy" | "warning" | "error";
  sites: {
    siteCode: string;
    totalDocs: number;
    lastSyncedAt: string;
    syncStatus: "synced" | "syncing" | "error" | "pending";
  }[];
}

// ── Folder structure for expandable rows ─────────────────────────────────────
export interface FolderItem {
  id: string;
  name: string;
  type: "folder" | "file";
  docCount?: number;
  buildStatus?: "succeeded" | "running" | "queued" | "failed" | "pending";
  children?: FolderItem[];
}

// ── Sync Path Config (for TeamsIntegration page) ────────────────────────────
// Each record represents ONE Teams Channel with selected folders to sync
export interface SelectedFolder {
  id: string;
  name: string; // e.g. "01_Protocol"
  docCount: number;
  buildStatus?: "succeeded" | "running" | "queued" | "failed" | "pending";
  folderStatus?: "active" | "unavailable"; // folder existence on SharePoint
  files?: FolderItem[]; // files inside this folder
  // Path validation status for async folder traversal after saving
  pathValidationStatus?: "validating" | "valid" | "invalid";
  pathValidationError?: string;
  // Subfolder paths to exclude from scanning/indexing under this folder
  exclusions?: string[];
}

export interface DocTagGroup {
  tagType: string;
  tags: string[];
}

export interface SyncPathConfig {
  id: number;
  studyCode: string;
  studyName: string;
  studyVersion: string;
  channelName: string; // Teams Channel display name
  channelUrl: string; // Teams Channel URL (teams.microsoft.com/l/channel/...)
  totalDocs: number;
  lastSyncedAt: string;
  syncStatus: "synced" | "syncing" | "error" | "pending";
  syncHealth: "healthy" | "warning" | "error";
  selectedFolders: SelectedFolder[]; // folders chosen during Add Channel flow
  // Source Settings metadata
  connectionType?: "teams" | "sharepoint";
  description?: string;
  agentTips?: string;
  docCategories?: string[];
  docTagGroups?: DocTagGroup[];
  isUserListSource?: boolean;
  visibleToUsers?: boolean;
  // legacy compat
  siteCode?: string;
  sharePointUrl?: string;
  folderStructure?: FolderItem[];
}

// ── Study Documents mock data ────────────────────────────────────────────────
export const mockStudyDocuments: StudyDocument[] = [
  // eVolve Meso — D7988C0001
  {
    id: "sd-001",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Protocol",
    name: "Protocol_v3.0_Amendment02.pdf",
    type: "pdf",
    size: "2.4 MB",
    syncedAt: "2026-03-15",
    versionDate: "2026-03-01",
    description: "Clinical trial protocol v3.0 with Amendment 02",
    version: "v3.0",
    site: "1301",
  },
  {
    id: "sd-002",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Protocol",
    name: "Protocol_Synopsis_v3.0.docx",
    type: "docx",
    size: "156 KB",
    syncedAt: "2026-03-15",
    versionDate: "2026-03-01",
    description: "Protocol synopsis document",
    version: "v3.0",
    site: "1301",
  },
  {
    id: "sd-003",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Protocol",
    name: "Protocol_v2.1_Archived.pdf",
    type: "pdf",
    size: "2.1 MB",
    syncedAt: "2025-09-20",
    versionDate: "2025-08-10",
    description: "Previous protocol version 2.1 (archived)",
    version: "v2.1",
    site: "1301",
  },
  {
    id: "sd-004",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "ICF",
    name: "ICF_v3.0_Chinese.pdf",
    type: "pdf",
    size: "890 KB",
    syncedAt: "2026-01-10",
    versionDate: "2025-12-20",
    description: "Informed consent form Chinese version v3.0",
    version: "v3.0",
    site: "1301",
  },
  {
    id: "sd-005",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "ICF",
    name: "ICF_v3.0_English.pdf",
    type: "pdf",
    size: "845 KB",
    syncedAt: "2026-01-10",
    versionDate: "2025-12-20",
    description: "Informed consent form English version v3.0",
    version: "v3.0",
    site: "1301",
  },
  {
    id: "sd-006",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Regulatory",
    name: "NMPA_Approval_Letter.pdf",
    type: "pdf",
    size: "680 KB",
    syncedAt: "2023-02-20",
    versionDate: "2023-02-15",
    description: "NMPA clinical trial approval letter",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-007",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Regulatory",
    name: "IEC_Approval_Site1302.pdf",
    type: "pdf",
    size: "445 KB",
    syncedAt: "2023-05-03",
    versionDate: "2023-04-28",
    description: "Site 1302 initial ethics committee approval",
    version: "v1.0",
    site: "1302",
  },
  {
    id: "sd-007b",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Regulatory",
    name: "IEC_Approval_Site1303.pdf",
    type: "pdf",
    size: "412 KB",
    syncedAt: "2023-06-10",
    versionDate: "2023-06-05",
    description: "Site 1303 initial ethics committee approval",
    version: "v1.0",
    site: "1303",
  },
  {
    id: "sd-008",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Regulatory",
    name: "IEC_Annual_Review_2025.pdf",
    type: "pdf",
    size: "312 KB",
    syncedAt: "2025-06-03",
    versionDate: "2025-05-28",
    description: "2025 annual continuing review approval",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-009",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Regulatory",
    name: "IEC_Amendment02_Approval.pdf",
    type: "pdf",
    size: "398 KB",
    syncedAt: "2026-01-08",
    versionDate: "2026-01-05",
    description: "Amendment 02 ethics committee approval",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-010",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Investigator's Brochure",
    name: "IB_v5.0.pdf",
    type: "pdf",
    size: "2.4 MB",
    syncedAt: "2026-03-05",
    versionDate: "2026-02-20",
    description: "Investigator's Brochure version 5.0",
    version: "v5.0",
    site: "1301",
  },
  {
    id: "sd-011",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Investigator's Brochure",
    name: "IB_v4.0_Archived.pdf",
    type: "pdf",
    size: "2.2 MB",
    syncedAt: "2025-06-10",
    versionDate: "2025-05-15",
    description: "Investigator's Brochure version 4.0 (archived)",
    version: "v4.0",
    site: "1301",
  },
  {
    id: "sd-012",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Safety",
    name: "SUSAR_Notification_03.pdf",
    type: "pdf",
    size: "234 KB",
    syncedAt: "2026-02-28",
    versionDate: "2026-02-25",
    description: "Sponsor SUSAR notification #3",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-013",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Safety",
    name: "SUSAR_Notification_02.pdf",
    type: "pdf",
    size: "218 KB",
    syncedAt: "2025-11-14",
    versionDate: "2025-11-10",
    description: "Sponsor SUSAR notification #2",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-014",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Monitoring Plan",
    name: "Monitoring_Plan_v2.1.pdf",
    type: "pdf",
    size: "445 KB",
    syncedAt: "2025-09-01",
    versionDate: "2025-08-20",
    description: "Monitoring plan with SDV ratio and frequency",
    version: "v2.1",
    site: "1301",
  },
  {
    id: "sd-014b",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Monitoring Plan",
    name: "Site1302_Monitoring_Record_V5.xlsx",
    type: "xlsx",
    size: "128 KB",
    syncedAt: "2026-03-26",
    versionDate: "2026-03-26",
    description: "Site 1302 Visit-5 on-site monitoring record",
    version: "v1.0",
    site: "1302",
    visit: "Visit-5th",
  },
  {
    id: "sd-014c",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Monitoring Plan",
    name: "Site1303_Monitoring_Record_V3.xlsx",
    type: "xlsx",
    size: "115 KB",
    syncedAt: "2026-02-20",
    versionDate: "2026-02-20",
    description: "Site 1303 Visit-3 on-site monitoring record",
    version: "v1.0",
    site: "1303",
    visit: "Visit-3rd",
  },
  {
    id: "sd-015",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Monitoring Plan",
    name: "Monitoring_Plan_v1.0_Archived.pdf",
    type: "pdf",
    size: "389 KB",
    syncedAt: "2023-06-15",
    versionDate: "2023-06-01",
    description: "Initial monitoring plan (archived)",
    version: "v1.0",
    site: "1301",
  },
  {
    id: "sd-016",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Insurance",
    name: "Insurance_Certificate_Site1302_2026.pdf",
    type: "pdf",
    size: "234 KB",
    syncedAt: "2026-01-01",
    versionDate: "2025-12-28",
    description: "Site 1302 subject insurance certificate valid until 2026-12",
    version: "v1.0",
    site: "1302",
  },
  {
    id: "sd-016b",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "Insurance",
    name: "Insurance_Certificate_Site1303_2026.pdf",
    type: "pdf",
    size: "229 KB",
    syncedAt: "2026-01-01",
    versionDate: "2025-12-28",
    description: "Site 1303 subject insurance certificate valid until 2026-12",
    version: "v1.0",
    site: "1303",
  },
  {
    id: "sd-017",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "SOP",
    name: "Sample_Processing_SOP_v3.0.pdf",
    type: "pdf",
    size: "567 KB",
    syncedAt: "2024-06-01",
    versionDate: "2024-05-15",
    description: "Biological sample collection and processing SOP",
    version: "v3.0",
    site: "1301",
  },
  {
    id: "sd-018",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    category: "SOP",
    name: "Emergency_Unblinding_SOP.pdf",
    type: "pdf",
    size: "345 KB",
    syncedAt: "2023-01-20",
    versionDate: "2023-01-10",
    description: "Emergency unblinding standard operating procedure",
    version: "v1.0",
    site: "1301",
  },

  // AURORA Trial — T3X9A1Y5
  {
    id: "sd-101",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Protocol",
    name: "AURORA_Protocol_v2.0.pdf",
    type: "pdf",
    size: "1.9 MB",
    syncedAt: "2026-02-10",
    versionDate: "2026-01-25",
    description: "AURORA trial protocol version 2.0",
    version: "v2.0",
    site: "101",
  },
  {
    id: "sd-102",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "ICF",
    name: "AURORA_ICF_v2.0_CN.pdf",
    type: "pdf",
    size: "760 KB",
    syncedAt: "2026-02-15",
    versionDate: "2026-02-01",
    description: "AURORA informed consent form Chinese v2.0",
    version: "v2.0",
    site: "101",
  },
  {
    id: "sd-103",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Regulatory",
    name: "AURORA_NMPA_Approval.pdf",
    type: "pdf",
    size: "590 KB",
    syncedAt: "2024-03-10",
    versionDate: "2024-03-05",
    description: "AURORA NMPA clinical trial approval",
    version: "v1.0",
    site: "101",
  },
  {
    id: "sd-103b",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Regulatory",
    name: "AURORA_IEC_Approval_Site201.pdf",
    type: "pdf",
    size: "410 KB",
    syncedAt: "2024-04-15",
    versionDate: "2024-04-10",
    description: "Site 201 IEC approval for AURORA trial",
    version: "v1.0",
    site: "201",
  },
  {
    id: "sd-104",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Investigator's Brochure",
    name: "AURORA_IB_v3.0.pdf",
    type: "pdf",
    size: "2.1 MB",
    syncedAt: "2026-01-20",
    versionDate: "2026-01-08",
    description: "AURORA Investigator's Brochure v3.0",
    version: "v3.0",
    site: "101",
  },
  {
    id: "sd-105",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Monitoring Plan",
    name: "AURORA_Monitoring_Plan_v1.2.pdf",
    type: "pdf",
    size: "398 KB",
    syncedAt: "2025-10-05",
    versionDate: "2025-09-20",
    description: "AURORA monitoring plan v1.2",
    version: "v1.2",
    site: "101",
  },
  {
    id: "sd-105b",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Monitoring Plan",
    name: "Site201_Monitoring_Record_V1.xlsx",
    type: "xlsx",
    size: "98 KB",
    syncedAt: "2026-03-08",
    versionDate: "2026-03-08",
    description: "Site 201 Visit-1 on-site monitoring record",
    version: "v1.0",
    site: "201",
    visit: "Visit-1st",
  },
  {
    id: "sd-106",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    category: "Safety",
    name: "AURORA_SUSAR_01.pdf",
    type: "pdf",
    size: "198 KB",
    syncedAt: "2025-12-18",
    versionDate: "2025-12-15",
    description: "AURORA SUSAR notification #1",
    version: "v1.0",
    site: "101",
  },
];

// ── Study Sync Status mock data ──────────────────────────────────────────────
export const mockStudySyncStatus: StudySyncStatus[] = [
  {
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Study",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aevolve-meso-study%40thread.tacv2/eVolve-Meso-Study?groupId=az-ct-group",
    totalDocs: 24,
    lastSyncedAt: "2026-03-26 14:32",
    syncStatus: "synced",
    syncHealth: "healthy",
    sites: [
      { siteCode: "1301", totalDocs: 14, lastSyncedAt: "2026-03-26 14:32", syncStatus: "synced" },
      { siteCode: "1302", totalDocs: 5, lastSyncedAt: "2026-03-26 14:32", syncStatus: "synced" },
      { siteCode: "1303", totalDocs: 5, lastSyncedAt: "2026-03-26 14:32", syncStatus: "synced" },
    ],
  },
  {
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "AURORA-Trial",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aaurora-trial%40thread.tacv2/AURORA-Trial?groupId=az-ct-group",
    totalDocs: 8,
    lastSyncedAt: "2026-03-08 09:15",
    syncStatus: "synced",
    syncHealth: "healthy",
    sites: [
      { siteCode: "101", totalDocs: 5, lastSyncedAt: "2026-03-08 09:15", syncStatus: "synced" },
      { siteCode: "201", totalDocs: 3, lastSyncedAt: "2026-03-08 09:15", syncStatus: "synced" },
    ],
  },
  {
    // Auto-named channel (no Channel Name entered by user), has Study Code
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    channelName: "Channel 03",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aaurora-site201%40thread.tacv2/General?groupId=az-ct-group",
    totalDocs: 6,
    lastSyncedAt: "2026-04-10 11:20",
    syncStatus: "synced",
    syncHealth: "healthy",
    sites: [
      { siteCode: "201", totalDocs: 6, lastSyncedAt: "2026-04-10 11:20", syncStatus: "synced" },
    ],
  },
  {
    // Has Channel Name, but NO Study Code (general shared folder, not tied to any study)
    studyCode: "",
    studyName: "General",
    channelName: "AZ-CRA-SOP-Library",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aaz-cra-sop%40thread.tacv2/AZ-CRA-SOP-Library?groupId=az-cra-team",
    totalDocs: 15,
    lastSyncedAt: "2026-04-15 09:00",
    syncStatus: "synced",
    syncHealth: "healthy",
    sites: [],
  },
  {
    // Auto-named, NO Study Code (general working standards)
    studyCode: "",
    studyName: "General",
    channelName: "Channel 05",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Ageneral-docs%40thread.tacv2/General?groupId=az-cra-team",
    totalDocs: 9,
    lastSyncedAt: "2026-04-20 14:45",
    syncStatus: "syncing",
    syncHealth: "warning",
    sites: [],
  },
  {
    // Has Channel Name, has Study Code, sync error
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    channelName: "eVolve-Meso-Site1304",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aevolve-meso-site1304%40thread.tacv2/eVolve-Meso-Site1304?groupId=az-ct-group",
    totalDocs: 0,
    lastSyncedAt: "—",
    syncStatus: "error",
    syncHealth: "error",
    sites: [],
  },
];

// ── Sync Path Config mock data ───────────────────────────────────────────────
// Each record = one Teams Channel with selected folders
export const mockSyncPathConfigs: SyncPathConfig[] = [
  {
    id: 1,
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    studyVersion: "v3.0",
    channelName: "eVolve-Meso-Study",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aevolve-meso-study%40thread.tacv2/eVolve-Meso-Study?groupId=az-ct-group",
    connectionType: "teams",
    description: "eVolve Meso 临床项目文档中心",
    docCategories: ["Protocol", "ICF", "Regulatory"],
    docTagGroups: [
      { tagType: "Indication", tags: ["Oncology"] },
      { tagType: "Study Code", tags: ["D7988C0001"] },
    ],
    isUserListSource: true,
    visibleToUsers: true,
    totalDocs: 14,
    lastSyncedAt: "2026-03-26 14:32",
    syncStatus: "synced",
    syncHealth: "healthy",
    selectedFolders: [
      {
        id: "sf-1-1",
        name: "01_Protocol",
        docCount: 3,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        exclusions: ["/Drafts", "/Archived"],
        files: [
          {
            id: "f-1-1-1",
            name: "Protocol_v3.0_Amendment02.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-1-1-2",
            name: "Protocol_Synopsis_v3.0.docx",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-1-1-3",
            name: "Protocol_v2.1_Archived.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
        ],
      },
      {
        id: "sf-1-2",
        name: "02_ICF",
        docCount: 2,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          { id: "f-1-2-1", name: "ICF_v3.0_Chinese.pdf", type: "file", buildStatus: "succeeded" },
          { id: "f-1-2-2", name: "ICF_v3.0_English.pdf", type: "file", buildStatus: "succeeded" },
        ],
      },
      {
        id: "sf-1-3",
        name: "03_Regulatory",
        docCount: 4,
        buildStatus: "succeeded",
        folderStatus: "unavailable",
        pathValidationStatus: "invalid",
        pathValidationError: "Folder path not found on SharePoint",
        files: [
          {
            id: "f-1-3-1",
            name: "NMPA_Approval_Letter.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-1-3-2",
            name: "IEC_Approval_Site1301.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-1-3-3",
            name: "IEC_Annual_Review_2025.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-1-3-4",
            name: "IEC_Amendment02_Approval.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
        ],
      },
      {
        id: "sf-1-4",
        name: "04_IB",
        docCount: 2,
        buildStatus: "running",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          { id: "f-1-4-1", name: "IB_v5.0.pdf", type: "file", buildStatus: "running" },
          { id: "f-1-4-2", name: "IB_v4.0_Archived.pdf", type: "file", buildStatus: "queued" },
        ],
      },
      {
        id: "sf-1-5",
        name: "05_Safety",
        docCount: 2,
        buildStatus: "queued",
        folderStatus: "active",
        pathValidationStatus: "valid",
        exclusions: ["/Internal_Notes", "/Drafts"],
        files: [
          { id: "f-1-5-1", name: "SUSAR_Notification_03.pdf", type: "file", buildStatus: "queued" },
          { id: "f-1-5-2", name: "SUSAR_Notification_02.pdf", type: "file", buildStatus: "queued" },
        ],
      },
      {
        id: "sf-1-6",
        name: "06_Monitoring",
        docCount: 1,
        buildStatus: "queued",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          { id: "f-1-6-1", name: "Monitoring_Plan_v2.1.pdf", type: "file", buildStatus: "queued" },
        ],
      },
    ],
  },
  {
    id: 2,
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    studyVersion: "v2.0",
    channelName: "AURORA-Trial",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aaurora-trial%40thread.tacv2/AURORA-Trial?groupId=az-ct-group",
    connectionType: "sharepoint",
    description: "AURORA Trial 文档库",
    docCategories: ["Protocol", "Safety"],
    docTagGroups: [{ tagType: "Indication", tags: ["Oncology"] }],
    isUserListSource: false,
    visibleToUsers: true,
    totalDocs: 8,
    lastSyncedAt: "2026-03-08 09:15",
    syncStatus: "synced",
    syncHealth: "healthy",
    selectedFolders: [
      {
        id: "sf-2-1",
        name: "01_Protocol",
        docCount: 1,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          {
            id: "f-4-1-1",
            name: "AURORA_Protocol_v2.0.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
        ],
      },
      {
        id: "sf-2-2",
        name: "02_ICF",
        docCount: 1,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        exclusions: ["/Old_Versions"],
        files: [
          { id: "f-4-2-1", name: "AURORA_ICF_v2.0_CN.pdf", type: "file", buildStatus: "succeeded" },
        ],
      },
      {
        id: "sf-2-3",
        name: "03_Regulatory",
        docCount: 2,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          {
            id: "f-4-3-1",
            name: "AURORA_NMPA_Approval.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-4-3-2",
            name: "AURORA_IEC_Approval_Site201.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
        ],
      },
      {
        id: "sf-2-4",
        name: "04_IB",
        docCount: 1,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        files: [
          { id: "f-4-4-1", name: "AURORA_IB_v3.0.pdf", type: "file", buildStatus: "succeeded" },
        ],
      },
      {
        id: "sf-2-5",
        name: "06_Monitoring",
        docCount: 2,
        buildStatus: "succeeded",
        folderStatus: "active",
        pathValidationStatus: "valid",
        exclusions: ["/Draft_Reports"],
        files: [
          {
            id: "f-4-5-1",
            name: "AURORA_Monitoring_Plan_v1.2.pdf",
            type: "file",
            buildStatus: "succeeded",
          },
          {
            id: "f-4-5-2",
            name: "Site201_Monitoring_Record_V1.xlsx",
            type: "file",
            buildStatus: "succeeded",
          },
        ],
      },
      {
        id: "sf-2-6",
        name: "07_Safety",
        docCount: 1,
        buildStatus: "succeeded",
        folderStatus: "unavailable",
        pathValidationStatus: "invalid",
        pathValidationError: "Folder path not found on SharePoint",
        files: [
          { id: "f-4-6-1", name: "AURORA_SUSAR_01.pdf", type: "file", buildStatus: "succeeded" },
        ],
      },
    ],
  },
  {
    id: 3,
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    studyVersion: "v2.0",
    channelName: "AURORA-Site301",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aaurora-site301%40thread.tacv2/AURORA-Site301?groupId=az-ct-group",
    connectionType: "teams",
    docCategories: ["Regulatory"],
    docTagGroups: [{ tagType: "Audience", tags: ["CRA"] }],
    isUserListSource: false,
    visibleToUsers: false,
    totalDocs: 0,
    lastSyncedAt: "2026-04-28 02:00",
    syncStatus: "error",
    syncHealth: "error",
    selectedFolders: [
      {
        id: "sf-3-1",
        name: "01_Protocol",
        docCount: 0,
        buildStatus: "failed",
        folderStatus: "unavailable",
        pathValidationStatus: "invalid",
        pathValidationError: "Folder path not found on SharePoint",
      },
      {
        id: "sf-3-2",
        name: "03_Regulatory",
        docCount: 0,
        buildStatus: "failed",
        folderStatus: "unavailable",
        pathValidationStatus: "invalid",
        pathValidationError: "Folder path not found on SharePoint",
      },
    ],
  },
  {
    id: 4,
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    studyVersion: "v3.0",
    channelName: "eVolve-Meso-Site1304",
    channelUrl:
      "https://teams.microsoft.com/l/channel/19%3Aevolve-meso-site1304%40thread.tacv2/eVolve-Meso-Site1304?groupId=az-ct-group",
    connectionType: "sharepoint",
    docCategories: ["ICF"],
    docTagGroups: [],
    isUserListSource: true,
    visibleToUsers: true,
    totalDocs: 0,
    lastSyncedAt: "—",
    syncStatus: "pending",
    syncHealth: "warning",
    selectedFolders: [
      {
        id: "sf-4-1",
        name: "01_Protocol",
        docCount: 0,
        buildStatus: "pending",
        folderStatus: "active",
        pathValidationStatus: "validating",
      },
      {
        id: "sf-4-2",
        name: "03_Regulatory",
        docCount: 0,
        buildStatus: "pending",
        folderStatus: "active",
        pathValidationStatus: "valid",
      },
      {
        id: "sf-4-3",
        name: "06_Monitoring",
        docCount: 0,
        buildStatus: "pending",
        folderStatus: "unavailable",
        pathValidationStatus: "invalid",
        pathValidationError: "Folder path not found on SharePoint",
      },
    ],
  },
];

// ── KB Build Task (Knowledge Base indexing task) ─────────────────────────────
export interface KBBuildTask {
  id: string;
  studyCode: string;
  studyName: string;
  siteCode: string;
  channelName: string;
  status: "running" | "queued" | "succeeded" | "failed";
  progress: number;
  totalDocs: number;
  processedDocs: number;
  startedAt: string;
  completedAt?: string;
  estimatedRemaining?: string;
}

// ── KB Build Task mock data ──────────────────────────────────────────────────
export const mockKBBuildTasks: KBBuildTask[] = [
  {
    id: "kb-001",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    siteCode: "1301",
    channelName: "eVolve-Meso-Study",
    status: "running",
    progress: 67,
    totalDocs: 14,
    processedDocs: 9,
    startedAt: "2026-04-28 02:15",
    estimatedRemaining: "约 3 分钟",
  },
  {
    id: "kb-002",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    siteCode: "1302",
    channelName: "eVolve-Meso-Study",
    status: "queued",
    progress: 0,
    totalDocs: 5,
    processedDocs: 0,
    startedAt: "2026-04-28 02:15",
  },
  {
    id: "kb-003",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    siteCode: "1303",
    channelName: "eVolve-Meso-Study",
    status: "succeeded",
    progress: 100,
    totalDocs: 5,
    processedDocs: 5,
    startedAt: "2026-04-28 02:03",
    completedAt: "2026-04-28 02:08",
  },
  {
    id: "kb-004",
    studyCode: "D7988C0001",
    studyName: "eVolve Meso",
    siteCode: "my-docs",
    channelName: "My-Documents-OneDrive",
    status: "running",
    progress: 42,
    totalDocs: 10,
    processedDocs: 4,
    startedAt: "2026-04-28 02:20",
    estimatedRemaining: "约 5 分钟",
  },
  {
    id: "kb-005",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    siteCode: "101",
    channelName: "AURORA-Trial",
    status: "succeeded",
    progress: 100,
    totalDocs: 5,
    processedDocs: 5,
    startedAt: "2026-04-28 02:03",
    completedAt: "2026-04-28 02:07",
  },
  {
    id: "kb-006",
    studyCode: "T3X9A1Y5",
    studyName: "AURORA Trial",
    siteCode: "201",
    channelName: "AURORA-Trial",
    status: "failed",
    progress: 30,
    totalDocs: 3,
    processedDocs: 1,
    startedAt: "2026-04-28 02:03",
    completedAt: "2026-04-28 02:05",
  },
];

// ── Shared helpers ───────────────────────────────────────────────────────────
export const STUDY_DOC_CATEGORIES = [
  "Protocol",
  "ICF",
  "Regulatory",
  "Investigator's Brochure",
  "Monitoring Plan",
  "Safety",
  "SOP",
  "Insurance",
];

export const TYPE_ICON: Record<DocFileType, string> = {
  pdf: "ri-file-pdf-line",
  xlsx: "ri-file-excel-line",
  xls: "ri-file-excel-line",
  docx: "ri-file-word-line",
  doc: "ri-file-word-line",
  pptx: "ri-file-ppt-line",
  pptm: "ri-file-ppt-line",
  ppt: "ri-file-ppt-line",
  csv: "ri-file-list-line",
  md: "ri-markdown-line",
  txt: "ri-file-text-line",
};

export const TYPE_COLOR: Record<DocFileType, string> = {
  pdf: "#dc2626",
  xlsx: "#16a34a",
  xls: "#16a34a",
  docx: "#2563eb",
  doc: "#2563eb",
  pptx: "#ea580c",
  pptm: "#ea580c",
  ppt: "#ea580c",
  csv: "#6b7280",
  md: "#ca8a04",
  txt: "#6b7280",
};

export const MOCK_STUDIES = [
  { code: "D7988C0001", name: "eVolve Meso", version: "v3.0" },
  { code: "T3X9A1Y5", name: "AURORA Trial", version: "v2.0" },
];

// ── Available Teams Channels per study ───────────────────────────────────────
export const MOCK_CHANNELS: Record<string, string[]> = {
  D7988C0001: [
    "eVolve-Meso-Study",
    "eVolve-Meso-Site1301",
    "eVolve-Meso-Site1302",
    "eVolve-Meso-Site1303",
    "eVolve-Meso-Site1304",
  ],
  T3X9A1Y5: ["AURORA-Trial", "AURORA-Site101", "AURORA-Site201", "AURORA-Site301"],
};

// ── Available Sites per study ─────────────────────────────────────────────────
export const MOCK_STUDY_SITES: Record<string, string[]> = {
  D7988C0001: ["1301", "1302", "1303", "1304", "1305", "1306", "1307"],
  T3X9A1Y5: ["101", "201", "301", "401"],
};

// ── Study-level Teams Channel URLs ───────────────────────────────────────────
export const STUDY_TEAMS_URLS: Record<string, string> = {
  D7988C0001:
    "https://teams.microsoft.com/l/channel/19%3Aevolve-meso-study-channel%40thread.tacv2/eVolve-Meso-Study?groupId=astrazeneca-ct",
  T3X9A1Y5:
    "https://teams.microsoft.com/l/channel/19%3Aaurora-trial-channel%40thread.tacv2/AURORA-Trial?groupId=astrazeneca-ct",
};

// ── File-level KB Index Status ─────────────────────────────────────────────────────
export interface FileKBStatus {
  fileId: string;
  status: "succeeded" | "running" | "queued" | "failed" | "pending";
  progress?: number;
  indexedAt?: string;
  errorMsg?: string;
}
